/* GHALIA — Cart drawer Quick Add (size / variant picker) */

(() => {
  const SKIP_OPTION_NAMES = [
    'material',
    'metal',
    'finish',
    'colour',
    'color',
    'الخامة',
    'لون',
    'اللون',
    'لونان',
  ];
  const SIZE_OPTION_HINTS = ['size', 'ring size', 'مقاس', 'مقاس الخاتم'];

  let deps = {};
  let state = {
    product: null,
    selected: [],
    variant: null,
    trigger: null,
    openCart: true,
  };

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const strings = () => window.Shopify?.strings?.quickAdd || {};

  const money = (cents) => {
    if (window.GhaliaPricing?.formatNet) return window.GhaliaPricing.formatNet(cents);
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    return (cents / 100).toFixed(2);
  };

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const shouldSkipOption = (name, values) => {
    const n = String(name || '')
      .toLowerCase()
      .trim();
    if (SKIP_OPTION_NAMES.some((s) => n.includes(s))) return true;
    if (n === 'title' && values?.length <= 1) return true;
    return false;
  };

  const normalizeOptionName = (raw, index) => {
    if (typeof raw === 'string') return raw.trim();
    if (raw && typeof raw === 'object') {
      return String(raw.name || raw.label || raw.title || '').trim();
    }
    return '';
  };

  const optionNameAt = (product, index) => {
    const raw = product?.options?.[index];
    const name = normalizeOptionName(raw, index);
    if (name) return name;
    const s = strings();
    return s.ringSize || s.sizeLabel || 'Size';
  };

  const looksLikeSizeValues = (values) =>
    (values || []).some((v) => /\(eu\)|\(us\)|\(uk\)/i.test(String(v)));

  const isSizeOption = (name, values) => {
    const n = String(name || '').toLowerCase();
    if (SIZE_OPTION_HINTS.some((hint) => n.includes(hint))) return true;
    return looksLikeSizeValues(values);
  };

  const getOptionValues = (product, optionIndex) => {
    const values = new Set();
    product.variants.forEach((variant) => {
      const val = [variant.option1, variant.option2, variant.option3][optionIndex];
      if (val) values.add(val);
    });
    return [...values];
  };

  const variantOptions = (variant) => {
    if (Array.isArray(variant.options) && variant.options.some((o) => o != null && o !== '')) {
      return variant.options;
    }
    return [variant.option1, variant.option2, variant.option3].filter((o) => o != null && o !== '');
  };

  const getSelectableOptions = (product) => {
    const names = product.options || [];
    const count = Math.max(
      names.length,
      ...product.variants.map((v) => variantOptions(v).length),
      0
    );
    return Array.from({ length: count }, (_, index) => {
      const values = getOptionValues(product, index);
      const name = optionNameAt(product, index);
      return { name, index, values };
    }).filter((opt) => !shouldSkipOption(opt.name, opt.values) && opt.values.length > 1);
  };

  const needsQuickAdd = (product) => getSelectableOptions(product).length > 0;

  const productMaterialLine = (product) => {
    const s = strings();
    const title = String(product?.title || '').toLowerCase();
    if (title.includes('sterling') && title.includes('gold')) return s.materialTwoTone || '';
    if (title.includes('sterling') || title.includes('silver') || title.includes('فض')) {
      return s.materialSterling || '';
    }
    if (title.includes('gold') || title.includes('ذهب')) return s.materialGold || '';
    return '';
  };

  const RING_SIZE_EU = {
    4: { eu: '47', label: 'Extra small' },
    4.5: { eu: '48', label: 'Extra small' },
    5: { eu: '50', label: 'XSmall' },
    5.5: { eu: '51', label: 'Small' },
    6: { eu: '52', label: 'Medium' },
    6.25: { eu: '53', label: 'Medium' },
    6.5: { eu: '53', label: 'Medium' },
    7: { eu: '54', label: 'Large' },
    7.5: { eu: '55', label: 'Large' },
    8: { eu: '57', label: 'Extra large' },
    8.5: { eu: '58', label: 'Extra large' },
    9: { eu: '59', label: 'XXL' },
    10: { eu: '61', label: 'XXL' },
  };

  const normalizeSizeLabel = (labelRaw) => {
    if (!labelRaw) return '';
    const raw = labelRaw.trim();
    const compact = raw.toLowerCase().replace(/\s+/g, '');
    if (/^(xs[-–]?small|xsmall|x-small|xs)$/.test(compact)) return 'XSmall';
    if (raw.includes('-')) {
      const first = raw.split('-')[0].trim().toLowerCase();
      if (first === 'xs') return 'XSmall';
      return raw.split('-').pop().trim();
    }
    return raw;
  };

  const lookupRingSizeMeta = (pillRaw) => {
    const us = String(pillRaw || '').trim();
    const num = parseFloat(us);
    if (Number.isNaN(num)) return { eu: '', label: '', us };
    const hit = RING_SIZE_EU[num] || RING_SIZE_EU[Math.round(num)];
    return hit ? { eu: hit.eu, label: hit.label, us } : { eu: '', label: '', us };
  };

  const parseRingSizeValue = (raw) => {
    if (!raw || typeof raw !== 'string') return { pill: '', eu: '', label: '', us: '' };
    const segments = raw.split('(').filter((p) => p.length);
    if (segments.length < 2) {
      const us = raw.trim();
      const meta = lookupRingSizeMeta(us);
      return { pill: us, eu: meta.eu, label: meta.label, us: meta.us || us };
    }
    const pill = (segments[0].split(')')[1] || segments[0] || '').trim().split('/')[0].trim();
    const labelRaw = segments[segments.length - 1].replace(/\)\s*$/, '').trim();
    const euPart = segments[segments.length - 2] || '';
    let eu = (euPart.split(')')[1] || '').trim().match(/\d{2,3}/)?.[0] || '';
    let label = normalizeSizeLabel(labelRaw);
    if (pill === '5' && eu === '50' && /^small$/i.test(label)) label = 'XSmall';
    if (!eu || !label) {
      const meta = lookupRingSizeMeta(pill);
      eu = eu || meta.eu;
      label = label || meta.label;
    }
    const usMatch = raw.match(/\(US\)\s*([\d.]+)/i);
    const us = usMatch ? usMatch[1] : pill;
    return { pill, eu, label, us };
  };

  const formatSizeLabelDisplay = (labelRaw) => {
    if (!labelRaw) return '';
    return String(labelRaw).trim().replace(/\s+/g, '').toUpperCase();
  };

  const formatUsSizeDetail = (template, us, labelRaw) => {
    const label = formatSizeLabelDisplay(labelRaw);
    let out = template
      .replace(/\{\{\s*us_size\s*\}\}/g, us)
      .replace(/\{\{\s*size_label\s*\}\}/g, label);
    if (!label) out = out.replace(/\s*·\s*$/g, '').replace(/\s*·\s*·/g, ' · ');
    return out.trim();
  };

  const pillDisplay = (value) => {
    const parsed = parseRingSizeValue(String(value || ''));
    if (parsed.eu) return parsed.eu;
    const raw = String(value || '').trim();
    const eu = raw.match(/\(EU\)\s*(\d+)/i) || raw.match(/EU\s*(\d+)/i);
    if (eu) return eu[1];
    const uk = raw.match(/\(UK\)\s*([A-Za-z]+)/i);
    if (uk) return uk[1].toUpperCase();
    const us = raw.match(/\(US\)\s*([\d.]+)/i);
    if (us) return us[1];
    const short = raw.split('(')[0].split('/').pop().trim();
    return short.length <= 4 ? short : raw.split('(')[0].split('/')[0].trim();
  };

  const sizeOptionLabel = (name, values, product) => {
    if (product) return sizeLabelForProduct(product, name, values);
    const s = strings();
    if (isSizeOption(name, values)) return s.ringSize || s.sizeLabel || name || 'Size';
    return name || 'Option';
  };

  const findVariant = (product, selected) => {
    const names = product.options || [];
    return product.variants.find((variant) => {
      const opts = variantOptions(variant);
      return names.every((_, idx) => {
        const pick = selected[idx];
        if (pick === undefined || pick === null || pick === '') return true;
        return opts[idx] === pick;
      });
    });
  };

  const isValueAvailable = (product, optionIndex, value, selected) =>
    product.variants.some((variant) => {
      if (!variant.available) return false;
      const opts = variantOptions(variant);
      if (opts[optionIndex] !== value) return false;
      return (product.options || []).every((_, idx) => {
        if (idx === optionIndex) return true;
        const pick = selected[idx];
        if (pick === undefined || pick === null || pick === '') return true;
        return opts[idx] === pick;
      });
    });

  const sizeSegment = (product) => {
    const type = String(product?.product_type || '').toLowerCase();
    const title = String(product?.title || '').toLowerCase();
    const tags = (product?.tags || []).map((t) => String(t).toLowerCase());
    if (type.includes('earring') || title.includes('earring') || tags.some((t) => t.includes('earring'))) {
      return 'earrings';
    }
    if (
      type.includes('necklace') ||
      title.includes('necklace') ||
      title.includes('choker') ||
      tags.some((t) => t.includes('necklace') || t.includes('choker'))
    ) {
      return 'necklaces';
    }
    if (type.includes('bracelet') || title.includes('bracelet') || tags.some((t) => t.includes('bracelet'))) {
      return 'bracelets';
    }
    if (type.includes('ring') || title.includes(' ring') || tags.some((t) => t.includes('ring'))) {
      return 'rings';
    }
    return 'rings';
  };

  const sizeLabelForProduct = (product, optionName, values) => {
    const s = strings();
    const segment = sizeSegment(product);
    if (!isSizeOption(optionName, values)) return optionName || 'Option';
    if (segment === 'bracelets') return s.braceletSize || s.sizeLabel || 'Bracelet size';
    if (segment === 'necklaces') return s.necklaceSize || s.sizeLabel || 'Necklace size';
    if (segment === 'earrings') return s.earringSize || s.sizeLabel || 'Earring size';
    return s.ringSize || s.sizeLabel || 'Ring size';
  };

  const pillDisplayFor = (product, value) => {
    if (sizeSegment(product) !== 'rings') return String(value || '').trim();
    return pillDisplay(value);
  };

  const getEls = () => {
    const root = deps.cartDrawer || document.querySelector('[data-cart-drawer]');
    if (!root) return null;
    return {
      root,
      panel: root.querySelector('[data-cart-quick-add]'),
      product: root.querySelector('[data-cart-quick-add-product]'),
      options: root.querySelector('[data-cart-quick-add-options]'),
      status: root.querySelector('[data-cart-quick-add-status]'),
      submit: root.querySelector('[data-cart-quick-add-submit]'),
      submitLabel: root.querySelector('[data-cart-quick-add-submit-label]'),
      wishlist: root.querySelector('[data-cart-quick-add-wishlist]'),
    };
  };

  const updateWishlistBtn = (els, product, variant) => {
    if (!els?.wishlist || !product) return;
    els.wishlist.hidden = false;
    els.wishlist.dataset.wishlistToggle = String(product.id);
    els.wishlist.dataset.productHandle = product.handle;
    els.wishlist.dataset.variantId = variant ? String(variant.id) : '';
    els.wishlist.dataset.productUrl = `/products/${product.handle}`;
  };

  const updateUI = () => {
    const els = getEls();
    const s = strings();
    if (!els?.panel || !state.product) return;

    const selectable = getSelectableOptions(state.product);
    const missingSize = selectable.some(
      (opt) =>
        isSizeOption(opt.name, opt.values) &&
        (state.selected[opt.index] == null || state.selected[opt.index] === '')
    );

    state.variant = findVariant(state.product, state.selected);

    if (els.product) {
      const image = state.variant?.featured_image?.src || state.product.featured_image || state.product.images?.[0];
      const imgSrc = image ? String(image).split('?')[0] + '?width=240' : '';
      const material = productMaterialLine(state.product);
      const price = state.variant?.price ?? state.product.variants?.[0]?.price ?? 0;
      const compare = state.variant?.compare_at_price;
      els.product.hidden = false;
      els.product.innerHTML = `
        <div class="cart-quick-add__product-media">
          ${imgSrc ? `<img src="${escapeHtml(imgSrc)}" alt="" width="88" height="88" loading="lazy" decoding="async">` : ''}
        </div>
        <div class="cart-quick-add__product-meta">
          <p class="cart-quick-add__product-title" dir="auto">${escapeHtml(state.product.title)}</p>
          ${material ? `<p class="cart-quick-add__product-material" dir="auto">${escapeHtml(material)}</p>` : ''}
          <p class="cart-quick-add__product-price" data-cart-quick-add-price>
            ${compare && compare > price ? `<del>${escapeHtml(money(compare))}</del>` : ''}
            <span>${escapeHtml(money(price))}</span>
          </p>
        </div>`;
    }

    if (els.options) {
      els.options.innerHTML = selectable
        .map((opt) => {
          const isSize = isSizeOption(opt.name, opt.values);
          const segment = sizeSegment(state.product);
          const optionLabel = sizeOptionLabel(opt.name, opt.values, state.product);
          const pills = opt.values
            .map((value, i) => {
              const id = `cart-qa-${opt.index}-${i}`;
              const checked = state.selected[opt.index] === value;
              const available = isValueAvailable(state.product, opt.index, value, state.selected);
              const pillText =
                isSize || looksLikeSizeValues([value])
                  ? pillDisplayFor(state.product, value)
                  : value;
              return `
                <label>
                  <input type="radio" name="cart-quick-add-opt-${opt.index}" id="${id}" value="${escapeHtml(value)}"
                    data-cart-quick-add-option data-option-index="${opt.index}"
                    ${checked ? 'checked' : ''} ${available ? '' : 'disabled'}>
                  <span class="option-pill option-pill--pdp">${escapeHtml(pillText)}</span>
                </label>`;
            })
            .join('');
          const sizeGuide = isSize
            ? `<button class="cart-quick-add__size-guide" type="button" data-drawer-open="[data-size-drawer]" data-size-segment="${sizeSegment(state.product)}" dir="auto">${escapeHtml(s.sizeGuide || 'Size guide')}</button>`
            : '';
          const sizeDetail =
            isSize && segment === 'rings'
              ? `<p class="cart-quick-add__size-detail" data-cart-quick-add-size-detail aria-live="polite" dir="auto"></p>`
              : '';
          return `
            <div class="cart-quick-add__option${isSize ? ' cart-quick-add__option--ring-size' : ''}${isSize && segment !== 'rings' ? ' cart-quick-add__option--apparel-size' : ''}">
              <div class="cart-quick-add__option-head">
                <span class="cart-quick-add__option-label t-cap-ltr" dir="auto">${escapeHtml(optionLabel)}:</span>
                ${sizeDetail}
              </div>
              <div class="option-pills option-pills--pdp cart-quick-add__pills" role="radiogroup" aria-label="${escapeHtml(optionLabel)}">
                ${pills}
              </div>
              ${sizeGuide}
            </div>`;
        })
        .join('');
    }

    if (els.status) {
      if (missingSize) {
        els.status.textContent = s.selectSize || 'Select size for availability';
        els.status.dataset.state = 'hint';
      } else if (!state.variant) {
        els.status.textContent = s.unavailable || 'Unavailable';
        els.status.dataset.state = 'error';
      } else if (!state.variant.available) {
        els.status.textContent = s.soldOut || 'Sold out';
        els.status.dataset.state = 'error';
      } else {
        els.status.textContent = '';
        els.status.dataset.state = '';
      }
    }

    if (els.submit) {
      const canAdd = state.variant?.available && !missingSize;
      els.submit.disabled = !canAdd;
      if (els.submitLabel) {
        els.submitLabel.textContent = canAdd
          ? s.addToBag || 'Add to Bag'
          : missingSize
            ? s.addToBag || 'Add to Bag'
            : s.soldOut || 'Sold out';
      }
    }

    updateWishlistBtn(els, state.product, state.variant);
    updateSizeDetail(els);
  };

  const updateSizeDetail = (els) => {
    const detail = els?.root?.querySelector('[data-cart-quick-add-size-detail]');
    if (!detail || !state.product) return;
    if (sizeSegment(state.product) !== 'rings') {
      detail.textContent = '';
      return;
    }
    const s = strings();
    let template = s.sizeDetailUs || 'US {{ us_size }} · {{ size_label }}';
    if (/translation missing/i.test(template)) template = 'US {{ us_size }} · {{ size_label }}';

    const checked = els.root?.querySelector('[data-cart-quick-add-option]:checked');
    if (!checked) {
      detail.textContent = '';
      return;
    }
    const parsed = parseRingSizeValue(checked.value);
    const us = parsed.us || parsed.pill || '';
    const sizeLabel = parsed.label || '';
    if (!us || !/^\d/.test(String(us).trim())) {
      detail.textContent = '';
      return;
    }
    detail.textContent = formatUsSizeDetail(template, us, sizeLabel);
  };

  const pickDefaultSelections = (product) => {
    const selected = Array.from(
      { length: Math.max((product.options || []).length, 3) },
      () => undefined
    );
    getSelectableOptions(product).forEach((opt) => {
      const firstAvailable = opt.values.find((value) =>
        isValueAvailable(product, opt.index, value, selected)
      );
      if (firstAvailable) selected[opt.index] = firstAvailable;
    });
    return selected;
  };

  const openPanel = (product, options = {}) => {
    const els = getEls();
    if (!els?.panel || !product) return;
    state = {
      product,
      selected: pickDefaultSelections(product),
      variant: null,
      trigger: options.trigger || null,
      openCart: options.openCart !== false,
    };
    els.root.classList.add('is-quick-add-open');
    els.panel.hidden = false;
    els.panel.dataset.open = 'true';
    els.panel.setAttribute('aria-hidden', 'false');
    updateUI();
  };

  const closePanel = () => {
    const els = getEls();
    const root = els?.root || document.querySelector('[data-cart-drawer]');
    root?.classList.remove('is-quick-add-open');
    if (!els?.panel) return;
    els.panel.hidden = true;
    els.panel.dataset.open = 'false';
    els.panel.setAttribute('aria-hidden', 'true');
    state = { product: null, selected: [], variant: null, trigger: null, openCart: true };
  };

  const fetchProduct = async (handle) => {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('Product not found');
    return res.json();
  };

  const resolveHandle = (btn) => {
    const direct = btn.getAttribute('data-quick-add-handle');
    if (direct) return direct.trim();
    const card = btn.closest('[data-product-card]');
    if (card?.dataset.productHandle) return card.dataset.productHandle.trim();
    const wishHandle = btn.dataset.productHandle;
    if (wishHandle) return wishHandle.trim();
    return '';
  };

  const handleQuickAddClick = async (btn) => {
    const variantId = btn.getAttribute('data-quick-add');
    const handle = resolveHandle(btn);
    const openCart = btn.getAttribute('data-open-cart') !== 'false';

    if (handle) {
      try {
        const product = await fetchProduct(handle);
        if (needsQuickAdd(product)) {
          if (deps.cartDrawer && deps.openDrawer) deps.openDrawer(deps.cartDrawer);
          openPanel(product, { trigger: btn, openCart });
          return true;
        }
        const fallbackId =
          variantId && product.variants?.some((v) => String(v.id) === String(variantId) && v.available)
            ? variantId
            : String(product.variants?.find((v) => v.available)?.id || '');
        if (fallbackId && deps.addVariantToCart) {
          await deps.addVariantToCart(fallbackId, btn, { openCart });
          return true;
        }
        const soldOut = window.Shopify?.strings?.quickAdd?.soldOut || 'Sold out';
        btn.dispatchEvent(new CustomEvent('ghalia:cart:error', { detail: new Error(soldOut), bubbles: true }));
      } catch (err) {
        console.error(err);
        btn.dispatchEvent(new CustomEvent('ghalia:cart:error', { detail: err, bubbles: true }));
        return true;
      }
    }

    if (variantId && deps.addVariantToCart) {
      await deps.addVariantToCart(variantId, btn, { openCart });
      return true;
    }
    return false;
  };

  const bind = () => {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-quick-add], [data-quick-add-handle]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      if (btn.getAttribute('aria-busy') === 'true') return;
      await handleQuickAddClick(btn);
    });

    document.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cart-quick-add-option]');
      if (!input || !state.product) return;
      const index = parseInt(input.dataset.optionIndex, 10);
      if (Number.isNaN(index)) return;
      state.selected[index] = input.value;
      updateUI();
    });

    document.addEventListener('click', async (e) => {
      const closeBtn = e.target.closest('[data-cart-quick-add-close]');
      if (closeBtn) {
        e.preventDefault();
        closePanel();
        return;
      }
      const submit = e.target.closest('[data-cart-quick-add-submit]');
      if (!submit || !deps.addVariantToCart) return;
      e.preventDefault();
      if (!state.product) return;
      if (!state.variant?.available) {
        updateUI();
        if (!state.variant?.available) return;
      }
      submit.setAttribute('aria-busy', 'true');
      try {
        await deps.addVariantToCart(String(state.variant.id), submit, { openCart: state.openCart });
        closePanel();
      } catch (err) {
        console.error(err);
      } finally {
        submit.removeAttribute('aria-busy');
      }
    });

    document.addEventListener('ghalia:cart-drawer:refreshed', closePanel);

    document.addEventListener('change', (e) => {
      const sizeInput = e.target.closest('[data-pdp-set-size]');
      if (!sizeInput) return;
      const root = sizeInput.closest('[data-pdp-set]');
      const addBtn = root?.querySelector('[data-pdp-set-add]');
      const variantId = sizeInput.dataset.setVariantId;
      if (addBtn && variantId) addBtn.setAttribute('data-quick-add', variantId);
    });

    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-pdp-bundle-add]');
      if (!btn || btn.getAttribute('aria-busy') === 'true') return;
      e.preventDefault();
      const members = String(btn.dataset.stackMembers || '')
        .split(',')
        .map((h) => h.trim());
      const anchor = String(btn.dataset.stackAnchor || '').trim();
      const nativeAnchors = window.GhaliaNativeBundleAnchors;
      if (nativeAnchors?.has(anchor) || members.some((h) => nativeAnchors?.has(h))) {
        window.alert('Use Add complete set above (with sizes) for this bundle.');
        return;
      }
      if (
        members.includes('infinity-bridge-ring') &&
        members.includes('single-infinity-pave-ring')
      ) {
        window.alert(
          'Use Add complete set above (with sizes) for the Infinity Ring Duo bundle.'
        );
        return;
      }
      if (
        members.includes('knot-bridge-ring-silver') &&
        members.includes('knot-bridge-bracelet-silver')
      ) {
        window.alert(
          'Use Add complete set above (with sizes) for the KNOT Bridge Ring + Bracelet duo.'
        );
        return;
      }
      const ids = String(btn.dataset.variantIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      if (!ids.length) return;
      btn.setAttribute('aria-busy', 'true');
      const stackProps = btn.dataset.stackAnchor
        ? {
            _ghalia_stack_anchor: btn.dataset.stackAnchor,
            _ghalia_stack_pct: String(btn.dataset.discountPct || ''),
            _ghalia_stack_members: btn.dataset.stackMembers || '',
            _ghalia_bundle_line: 'true',
          }
        : null;
      try {
        if (window.GhaliaCart?.addItems) {
          await window.GhaliaCart.addItems(
            ids.map((id) => ({
              id: Number(id),
              quantity: 1,
              ...(stackProps ? { properties: { ...stackProps } } : {}),
            })),
            btn,
            { openCart: true }
          );
        } else {
          const root = window.Shopify?.routes?.root || '/';
          const res = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: ids.map((id) => ({ id: Number(id), quantity: 1 })) }),
          });
          if (!res.ok) throw new Error('Could not add stack');
          if (deps.openDrawer && deps.cartDrawer) deps.openDrawer(deps.cartDrawer);
        }
      } catch (err) {
        console.error(err);
      } finally {
        btn.removeAttribute('aria-busy');
      }
    });
  };

  window.GhaliaCartQuickAdd = {
    init(api) {
      deps = api || {};
      bind();
    },
    close: closePanel,
    needsQuickAdd,
    open: openPanel,
  };

  if (window.GhaliaCartQuickAddDeps) {
    window.GhaliaCartQuickAdd.init(window.GhaliaCartQuickAddDeps);
  }
})();
